import { useCallback, useEffect, useMemo, useState } from "react";
import type { Bank, RenderScanabilityReport, RenderWarning, TemplateSummary, VietQRData } from "./api";
import { getBanks } from "./api";
import BankStep from "./components/BankStep";
import InfoStep from "./components/InfoStep";
import ConfirmStep from "./components/ConfirmStep";
import TemplateStep from "./components/TemplateStep";
import ResultStep from "./components/ResultStep";
import PhotoFlow from "./components/PhotoFlow";

type Step = "bank" | "info" | "confirm" | "template" | "photo" | "result";

type TemplateInfo = { templateId: string; version: number; name: string; aspectRatio: string };

type ResultData = {
  svg: string;
  payload: string;
  warnings: RenderWarning[];
  template: TemplateInfo | null;
  /** `null` khi server chưa kiểm tra được độ an toàn khi quét. */
  scanability: RenderScanabilityReport | null;
};

export default function App() {
  const [banks, setBanks] = useState<Bank[] | null>(null);
  const [banksLoading, setBanksLoading] = useState(true);
  const [banksError, setBanksError] = useState<string | null>(null);

  const [step, setStep] = useState<Step>("bank");
  const [bank, setBank] = useState<Bank | null>(null);
  const [accountNumber, setAccountNumber] = useState("");
  const [amount, setAmount] = useState<number | undefined>(undefined);
  const [purpose, setPurpose] = useState("");

  const [result, setResult] = useState<ResultData | null>(null);

  const loadBanks = useCallback(() => {
    setBanksLoading(true);
    setBanksError(null);
    getBanks()
      .then((res) => setBanks(res.banks))
      .catch(() => {
        setBanksError("Mạng đang chập chờn, mình chưa tải được danh sách ngân hàng. Thử lại giúp mình nhé.");
      })
      .finally(() => setBanksLoading(false));
  }, []);

  useEffect(() => {
    loadBanks();
  }, [loadBanks]);

  const qrData: VietQRData | null = useMemo(() => {
    if (!bank) return null;
    return { bankBin: bank.bin, accountNumber, amount, purpose: purpose || undefined };
  }, [bank, accountNumber, amount, purpose]);

  function handleSelectBank(b: Bank) {
    setBank(b);
    setStep("info");
  }

  function handleConfirm() {
    // Bước chọn theme sẽ render mã thật bằng dữ liệu ngân hàng này.
    setStep("template");
  }

  function handleSelectTemplate(
    template: TemplateSummary,
    svg: string,
    payload: string,
    warnings: RenderWarning[],
    scanability: RenderScanabilityReport | null,
  ) {
    setResult({
      svg,
      payload,
      warnings,
      scanability,
      template: {
        templateId: template.templateId,
        version: template.version,
        name: template.name,
        aspectRatio: template.aspectRatio,
      },
    });
    setStep("result");
  }

  function handleReset() {
    setStep("bank");
    setBank(null);
    setAccountNumber("");
    setAmount(undefined);
    setPurpose("");
    setResult(null);
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <span className="app-title">QR Studio · Theme & Ảnh</span>
      </header>

      <main className="app-main">
        {step === "bank" ? (
          <BankStep
            banks={banks}
            loading={banksLoading}
            error={banksError}
            onRetry={loadBanks}
            onSelect={handleSelectBank}
          />
        ) : null}

        {step === "info" && bank ? (
          <InfoStep
            bank={bank}
            onChangeBank={() => setStep("bank")}
            accountNumber={accountNumber}
            onAccountNumberChange={setAccountNumber}
            amount={amount}
            onAmountChange={setAmount}
            purpose={purpose}
            onPurposeChange={setPurpose}
            onContinue={() => setStep("confirm")}
          />
        ) : null}

        {step === "confirm" && bank ? (
          <ConfirmStep
            bank={bank}
            accountNumber={accountNumber}
            amount={amount}
            purpose={purpose}
            onEdit={() => setStep("info")}
            onConfirm={handleConfirm}
            submitting={false}
          />
        ) : null}

        {step === "template" && qrData ? (
          <TemplateStep
            qrData={qrData}
            onBack={() => setStep("confirm")}
            onSelectTemplate={handleSelectTemplate}
            onUsePhoto={() => setStep("photo")}
          />
        ) : null}

        {step === "photo" && bank && qrData ? (
          <PhotoFlow
            bank={bank}
            accountNumber={accountNumber}
            amount={amount}
            qrData={qrData}
            onBack={() => setStep("template")}
            onReset={handleReset}
          />
        ) : null}

        {step === "result" && bank && qrData && result ? (
          <ResultStep
            bank={bank}
            accountNumber={accountNumber}
            amount={amount}
            qrData={qrData}
            svg={result.svg}
            payload={result.payload}
            template={result.template}
            warnings={result.warnings}
            scanability={result.scanability}
            onReset={handleReset}
            onChangeTemplate={() => setStep("template")}
          />
        ) : null}
      </main>
    </div>
  );
}
